import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { format } from 'date-fns';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import dashboardAPI from '../../services/dashboard';
import PaginationComponent from '../PaginationComponent';

/**
 * #021749 (BACOFFEE-20260908-005914-1) — "Xem lịch sử biến động Tiền Két".
 *
 * Góp ý gốc qua nút Trợ năng (trang chủ Quản lý, khổ mobile): bấm vào ô tiền để xem sổ quỹ —
 * mỗi lần CHI ghi rõ nội dung + số tiền, xen giữa là TỔNG doanh thu gộp theo đợt (không phải
 * từng đơn hàng lẻ), trình bày kiểu danh sách theo mốc thời gian như tab "Lịch sử tỷ lệ thưởng".
 *
 * Khuôn modal bám đúng `PendingReceiptsModal` (#182201) — cùng cỡ `xl centered scrollable`, cùng
 * khối tóm tắt đầu bảng, cùng cặp bảng-máy-tính / thẻ-điện-thoại, cùng `PaginationComponent`.
 * Không tự chế kiểu mới (luật #731598018/019).
 *
 * Việc GỘP doanh thu do SERVER làm (window function trong `getCashBalanceHistory`), không gộp ở
 * đây: gộp ở FE thì phân trang đếm theo bản ghi thô, một đợt doanh thu bị cắt ngang giữa 2 trang
 * và con số tổng trên màn sẽ sai.
 *
 * #153102 (08/09/2026) từng nhét CẢ khối "Tổng hợp Két theo kỳ" (4 số đầu kỳ/doanh thu/chi/cuối
 * kỳ) cùng bộ chọn kỳ vào đầu modal này.
 *
 * #175410 (góp ý Spot Fix 3f65c9fe, 10/09/2026, nguyên văn *"bỏ cái này đi, chỉ giữ lại cái lịch
 * sử ở dưới thôi.."*) — GỠ khối tổng hợp đó khỏi modal, chỉ còn LỊCH SỬ biến động. Bộ chọn kỳ
 * ("Kỳ đang xem" + nút Đổi kỳ + DateRangePicker) gỡ theo vì nó CHỈ nuôi khối tổng hợp: phần lịch
 * sử không lọc theo kỳ (`getCashBalanceHistory` chỉ nhận page/limit/branch_id), giữ lại thì thành
 * nút bấm không đổi gì trên màn. Component `CashPeriodSummary` và API `getCashPeriodSummary` GIỮ
 * NGUYÊN, không xoá — chỉ thôi hiển thị ở đây.
 *
 * Phần LỊCH SỬ vẫn KHÔNG lọc theo kỳ — giữ nguyên hành vi đã lên production của #021749.
 *
 * #182734 (lệnh sếp 17:5x 08/09/2026) — lịch sử nay DỰNG TỪ DỮ LIỆU CŨ CÓ SẴN, không còn bắt đầu
 * từ ngày bật tính năng. Nguyên văn: *"lấy dữ liệu từ doanh thu chi phí và những lần nhập két đóng
 * ca mở ca để hiển thị ra lịch sử chứ không phải là bắt đầu tạo từ sau khi cập nhật tính năng"*.
 * Ba thay đổi ở màn này:
 *  - bỏ hẳn câu "các thay đổi trước mốc này không được lưu nên không dựng lại được" (nay sai);
 *  - thêm cột **Người thao tác** — sếp yêu cầu mỗi dòng phải cho biết ai làm;
 *  - dòng dựng lại từ dữ liệu cũ được đánh dấu "suy ra" để người xem biết nó không phải bản ghi
 *    trực tiếp lúc biến động xảy ra (số đúng, nhưng nguồn là bản ghi nghiệp vụ chứ không phải sổ).
 *
 * #232745 (BACOFFEE-20260908-232648-1) — doanh thu nay gộp theo **CA**, không theo đợt đơn liền
 * nhau. Nguyên văn góp ý: *"gom lại thành tổng doanh thu ở trong từng ca, phiếu chi thì không gộp
 * mà hiển thị chi tiết"*. Việc gộp vẫn do SERVER làm (lý do phân trang ở trên, không đổi); màn này
 * chỉ đọc thêm cờ `is_shift_group` để ghi "Cả ca" thay vì "Gộp".
 *
 * #131506 — dòng "Tổng doanh thu tiền mặt (N đơn)" nay BẤM ĐƯỢC, mở danh sách TỪNG ĐƠN cấu thành
 * con số đó. Đây là câu cuối của chính góp ý gốc #021749 (*"bấm vào phần doanh thu 2.000.000 đó
 * sẽ hiển thị chi tiết tất cả các đơn cấu thành nên 2.000.000 đó"*) đã bị cắt cụt lúc viết yêu
 * cầu, khiến #021749 làm NGƯỢC lại ("cố ý không liệt kê từng đơn").
 *
 * Danh sách hiện THAY CHỖ phần lịch sử trong cùng modal (không mở modal chồng modal): người dùng
 * đang đọc sổ quỹ, mở thêm một lớp nữa là mất mạch. Bấm "Quay lại" là về đúng trang cũ của lịch
 * sử — state trang/kỳ không bị đụng nên không phải tải lại gì.
 *
 * ⚠️ Hệ quả phải biết khi đọc cột "Số dư sau": cụm gộp theo ca được phép ĐỨT QUÃNG (phiếu chi nằm
 * xen giữa vẫn là dòng riêng), nên số dư của dòng gộp là số dư sau LẦN THU CUỐI của ca — nó đã bao
 * gồm cả các phiếu chi xen giữa. Vì vậy "số dư dòng trên + số tiền dòng dưới" KHÔNG còn luôn bằng
 * số dư dòng dưới như hồi gộp theo đoạn liền nhau. Đây là đánh đổi cố ý của chính yêu cầu này:
 * người dùng muốn mỗi ca đúng MỘT dòng doanh thu, mà vẫn thấy từng phiếu chi.
 */

const LIMIT = 20;

const formatCurrency = (value) =>
  new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'HH:mm dd/MM/yyyy');
  } catch {
    return value;
  }
};

const AMOUNT_STYLE = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

const KIND_META = {
  REVENUE_GROUP: { label: 'Doanh thu', variant: 'success' },
  VOUCHER: { label: 'Phiếu chi', variant: 'danger' },
  SHIFT: { label: 'Chốt ca', variant: 'info' },
  MANUAL: { label: 'Sửa tay', variant: 'secondary' },
};

/** Nội dung một dòng — phiếu chi ưu tiên mô tả thật của phiếu, không phải mã phiếu. */
const rowTitle = (row) => {
  if (row.kind === 'VOUCHER' && row.voucher_description) {
    return row.voucher_description;
  }
  return row.reason;
};

const rowSubtitle = (row) => {
  if (row.kind === 'VOUCHER' && row.voucher_number) return `Phiếu ${row.voucher_number}`;
  if (row.kind === 'REVENUE_GROUP' && row.from_at) {
    return `Gộp ${row.entry_count} lần thu, từ ${formatDateTime(row.from_at)}`;
  }
  return null;
};

/**
 * #182734 — nhãn "suy ra" cho dòng dựng lại từ dữ liệu cũ. Cố ý là nhãn nhỏ màu nhạt chứ không
 * phải cảnh báo: số liệu vẫn đúng và đã đối chiếu ngược ra bảng gốc, chỉ khác NGUỒN — nên không
 * được làm người xem tưởng dòng đó đáng ngờ.
 */
const BackfilledTag = () => (
  <span className="badge bg-light text-muted border ms-1 fw-normal" title="Dòng này được dựng lại từ đơn hàng / phiếu chi / mở ca / đóng ca đã có sẵn, không phải ghi trực tiếp lúc biến động xảy ra">
    suy ra
  </span>
);

const AmountCell = ({ amount }) => {
  const positive = Number(amount) >= 0;
  return (
    <span className={positive ? 'text-success fw-bold' : 'text-danger fw-bold'} style={AMOUNT_STYLE}>
      {positive ? '+' : '−'}
      {formatCurrency(Math.abs(amount))}đ
    </span>
  );
};

const DETAIL_LIMIT = 20;

/**
 * #131506 — danh sách các đơn cấu thành MỘT dòng doanh thu gộp.
 *
 * Hai con số ở đầu (tổng tiền + số đơn) lấy từ `group` do SERVER tính lại trên chính cụm đó, chứ
 * không cộng từ trang đang hiển thị: có phân trang nên trang hiện tại chỉ là một phần, mà thứ
 * người dùng cần đối chiếu là TỔNG của cả cụm so với dòng gộp họ vừa bấm.
 */
const RevenueGroupDetail = ({ row, branchId, allBranches, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(1);

  const entryId = String(row.key).replace(/^rev-/, '');

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dashboardAPI.getCashRevenueGroupOrders({
        entry_id: entryId,
        page,
        limit: DETAIL_LIMIT,
        ...(branchId ? { branch_id: branchId } : {}),
      });
      setResult(res);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Không tải được danh sách đơn của đợt doanh thu này',
      );
    } finally {
      setLoading(false);
    }
  }, [entryId, page, branchId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const orders = result?.data || [];
  const group = result?.group;
  const total = result?.meta?.total ?? 0;

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <Button size="sm" variant="outline-secondary" onClick={onBack}>
          <ArrowLeft size={14} className="me-1" />
          Quay lại lịch sử
        </Button>
        <div className="small text-muted">{rowSubtitle(row) || formatDateTime(row.occurred_at)}</div>
      </div>

      <div className="p-3 bg-light rounded mb-3">
        <div className="text-muted small">Các đơn cấu thành dòng doanh thu này</div>
        <div className="h5 mb-0" style={AMOUNT_STYLE}>
          {formatCurrency(group?.amount ?? row.amount)}đ
          <span className="text-muted fs-6 fw-normal ms-2">
            · {group?.entry_count ?? row.entry_count} đơn
          </span>
        </div>
        {group?.branch_name && (
          <div className="small text-muted mt-1">{group.branch_name}</div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" /> Đang tải danh sách đơn...
        </div>
      ) : error ? (
        <Alert variant="danger" className="mb-0">
          {error}
          <div className="mt-2">
            <Button size="sm" variant="outline-danger" onClick={fetchDetail}>
              Thử lại
            </Button>
          </div>
        </Alert>
      ) : total === 0 ? (
        <div className="text-center text-muted py-5">Không còn đơn nào trong đợt này.</div>
      ) : (
        <>
          {/* Máy tính: bảng */}
          <div className="d-none d-md-block table-responsive">
            <Table hover size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Thời điểm</th>
                  <th>Đơn</th>
                  <th>Bàn</th>
                  <th>Khách</th>
                  {allBranches && <th>Chi nhánh</th>}
                  <th className="text-end">Tiền mặt</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.ledger_id}>
                    <td className="text-nowrap small">{formatDateTime(o.occurred_at)}</td>
                    <td className="small">
                      {o.order_number || <span className="text-muted">{o.reason}</span>}
                      {o.is_backfilled && <BackfilledTag />}
                    </td>
                    <td className="small">
                      {o.table_number ? `Bàn ${o.table_number}` : <span className="text-muted">—</span>}
                    </td>
                    <td className="small">
                      {o.customer_name || <span className="text-muted">—</span>}
                    </td>
                    {allBranches && <td className="small">{o.branch_name}</td>}
                    <td className="text-end">
                      <AmountCell amount={o.amount} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Điện thoại: thẻ — góp ý gốc đến từ khổ mobile nên khổ này phải đọc được trước */}
          <div className="d-md-none">
            {orders.map((o) => (
              <div key={o.ledger_id} className="border-bottom py-2">
                <div className="d-flex justify-content-between gap-2">
                  <span className="small text-muted">{formatDateTime(o.occurred_at)}</span>
                  <AmountCell amount={o.amount} />
                </div>
                <div>
                  {o.order_number || o.reason}
                  {o.is_backfilled && <BackfilledTag />}
                </div>
                <div className="small text-muted">
                  {allBranches ? `${o.branch_name} · ` : ''}
                  {o.table_number ? `Bàn ${o.table_number}` : 'Không gắn bàn'}
                  {o.customer_name ? ` · ${o.customer_name}` : ''}
                </div>
              </div>
            ))}
          </div>

          {total > DETAIL_LIMIT && (
            <div className="mt-3">
              <PaginationComponent
                totalItems={total}
                itemsPerPage={DETAIL_LIMIT}
                currentPage={page}
                onPageChange={setPage}
                loading={loading}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

const CashBalanceHistoryModal = ({ show, onHide, branchId, branchName }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(1);
  // #131506 — dòng doanh thu gộp đang mở chi tiết (null = đang xem danh sách lịch sử). Giữ cả
  // DÒNG chứ không chỉ khoá: panel chi tiết cần nhãn + số của dòng để người xem đối chiếu ngay.
  const [openGroup, setOpenGroup] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dashboardAPI.getCashBalanceHistory({
        page,
        limit: LIMIT,
        ...(branchId ? { branch_id: branchId } : {}),
      });
      setResult(res);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Không tải được lịch sử biến động Tiền Két',
      );
    } finally {
      setLoading(false);
    }
  }, [page, branchId]);

  useEffect(() => {
    if (!show) return;
    fetchData();
  }, [show, fetchData]);

  // Mở lại modal thì luôn về trang 1 để không thấy trang cũ của lần xem trước.
  useEffect(() => {
    if (!show) return;
    setPage(1);
    setOpenGroup(null);
  }, [show, branchId]);

  // #131506 — mở dòng gộp: chỉ REVENUE_GROUP mới có chi tiết đơn, các loại dòng khác vốn đã là
  // một bản ghi nghiệp vụ duy nhất, không có gì để tách nhỏ thêm.
  const openable = (row) => row.kind === 'REVENUE_GROUP';
  const openDetail = (row) => {
    if (openable(row)) setOpenGroup(row);
  };
  const onRowKeyDown = (row) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDetail(row);
    }
  };

  const rows = result?.data || [];
  const total = result?.meta?.total ?? 0;
  // Xem "Tất cả chi nhánh" thì số dư là TỔNG nhiều két khác nhau — phải nói rõ, và mỗi dòng phải
  // kèm tên chi nhánh, nếu không cột "Số dư sau" nhìn như nhảy loạn.
  const allBranches = !branchId;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="h5 mb-0">Tiền Két</Modal.Title>
          <div className="small text-muted">
            {branchName || 'Tất cả chi nhánh'} · từng lần cộng/trừ tiền
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* ===== LỊCH SỬ từng lần biến động (#021749, giữ nguyên) ===== */}
        {/* #131506 — mở một dòng doanh thu gộp thì phần này nhường chỗ cho danh sách đơn của nó;
            state trang của lịch sử giữ nguyên nên bấm "Quay lại" là về đúng chỗ cũ. */}
        {openGroup ? (
          <>
            <h6 className="mb-3">🧾 Chi tiết đơn của đợt doanh thu</h6>
            <RevenueGroupDetail
              row={openGroup}
              branchId={branchId}
              allBranches={allBranches}
              onBack={() => setOpenGroup(null)}
            />
          </>
        ) : (
          <>
        <h6 className="mb-3">📜 Lịch sử biến động</h6>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" size="sm" /> Đang tải lịch sử biến động...
          </div>
        ) : error ? (
          <Alert variant="danger" className="mb-0">
            {error}
            <div className="mt-2">
              <Button size="sm" variant="outline-danger" onClick={fetchData}>
                Thử lại
              </Button>
            </div>
          </Alert>
        ) : (
          <>
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3 p-3 bg-light rounded">
              <div>
                <div className="text-muted small">
                  {allBranches ? 'Tổng số dư các két' : 'Số dư két hiện tại'}
                </div>
                <div className="h4 mb-0" style={AMOUNT_STYLE}>
                  {formatCurrency(result?.current_balance)}đ
                </div>
              </div>
              <div className="small text-muted">{total} mốc biến động</div>
            </div>

            {/* #182734 — nói đúng độ tin cậy của số: phần cũ là SUY RA từ đơn hàng / phiếu chi /
                mở ca / đóng ca đã có sẵn, phần mới là ghi trực tiếp lúc biến động xảy ra. */}
            {result?.earliest_recorded_at && (
              <div className="small text-muted mb-2">
                Lịch sử chạy ngược tới {formatDateTime(result.earliest_recorded_at)}.
                {result?.earliest_live_recorded_at ? (
                  <>
                    {' '}Các mốc trước {formatDateTime(result.earliest_live_recorded_at)} được{' '}
                    <strong>dựng lại</strong> từ đơn hàng tiền mặt, phiếu chi, tiền đầu ca và chốt
                    ca đã có sẵn trong hệ thống.
                  </>
                ) : (
                  <>
                    {' '}Toàn bộ được <strong>dựng lại</strong> từ đơn hàng tiền mặt, phiếu chi,
                    tiền đầu ca và chốt ca đã có sẵn trong hệ thống.
                  </>
                )}
              </div>
            )}

            {total === 0 ? (
              <div className="text-center text-muted py-5">
                Chưa ghi nhận biến động nào ở chi nhánh đang chọn.
              </div>
            ) : (
              <>
                {/* Máy tính: bảng */}
                <div className="d-none d-md-block table-responsive">
                  <Table hover size="sm" className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Thời điểm</th>
                        <th>Nội dung</th>
                        <th>Người thao tác</th>
                        {allBranches && <th>Chi nhánh</th>}
                        <th className="text-end">Thay đổi</th>
                        <th className="text-end">Số dư sau</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const meta = KIND_META[row.kind] || {};
                        const subtitle = rowSubtitle(row);
                        const canOpen = openable(row);
                        return (
                          <tr
                            key={row.key}
                            onClick={canOpen ? () => openDetail(row) : undefined}
                            onKeyDown={canOpen ? onRowKeyDown(row) : undefined}
                            role={canOpen ? 'button' : undefined}
                            tabIndex={canOpen ? 0 : undefined}
                            style={canOpen ? { cursor: 'pointer' } : undefined}
                            title={canOpen ? 'Bấm để xem từng đơn cấu thành số này' : undefined}
                          >
                            <td className="text-nowrap small">{formatDateTime(row.occurred_at)}</td>
                            <td>
                              <Badge bg={meta.variant || 'secondary'} className="me-2">
                                {meta.label || row.kind}
                              </Badge>
                              {rowTitle(row)}
                              {row.is_backfilled && <BackfilledTag />}
                              {subtitle && <div className="small text-muted">{subtitle}</div>}
                              {canOpen && (
                                <div className="small text-primary">
                                  Xem {row.entry_count} đơn cấu thành
                                  <ChevronRight size={14} />
                                </div>
                              )}
                            </td>
                            <td className="small">
                              {row.actor_name || <span className="text-muted">Không rõ</span>}
                            </td>
                            {allBranches && <td className="small">{row.branch_name}</td>}
                            <td className="text-end">
                              <AmountCell amount={row.amount} />
                            </td>
                            <td className="text-end" style={AMOUNT_STYLE}>
                              {formatCurrency(row.balance_after)}đ
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                {/* Điện thoại: thẻ — góp ý gốc đến từ khổ mobile nên khổ này phải đọc được trước */}
                <div className="d-md-none">
                  {rows.map((row) => {
                    const meta = KIND_META[row.kind] || {};
                    const subtitle = rowSubtitle(row);
                    const canOpen = openable(row);
                    return (
                      <div
                        key={row.key}
                        className="border-bottom py-2"
                        onClick={canOpen ? () => openDetail(row) : undefined}
                        onKeyDown={canOpen ? onRowKeyDown(row) : undefined}
                        role={canOpen ? 'button' : undefined}
                        tabIndex={canOpen ? 0 : undefined}
                      >
                        <div className="d-flex justify-content-between gap-2">
                          <span className="small text-muted">{formatDateTime(row.occurred_at)}</span>
                          <AmountCell amount={row.amount} />
                        </div>
                        <div>
                          <Badge bg={meta.variant || 'secondary'} className="me-2">
                            {meta.label || row.kind}
                          </Badge>
                          {rowTitle(row)}
                          {row.is_backfilled && <BackfilledTag />}
                        </div>
                        {subtitle && <div className="small text-muted">{subtitle}</div>}
                        <div className="small text-muted">
                          {allBranches ? `${row.branch_name} · ` : ''}
                          {row.actor_name ? `${row.actor_name} · ` : ''}
                          Số dư sau: <strong>{formatCurrency(row.balance_after)}đ</strong>
                        </div>
                        {canOpen && (
                          <div className="small text-primary">
                            Xem {row.entry_count} đơn cấu thành
                            <ChevronRight size={14} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {total > LIMIT && (
                  <div className="mt-3">
                    <PaginationComponent
                      totalItems={total}
                      itemsPerPage={LIMIT}
                      currentPage={page}
                      onPageChange={setPage}
                      loading={loading}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Đóng
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CashBalanceHistoryModal;
